import {
  Button,
  Card,
  CardHeader,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  Radio,
  RadioGroup,
  Skeleton,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import React, { useEffect, useState } from "react";
import {
  getStockOrderProducts,
  selectGetStockOrderProducts,
} from "../slices/get-products.slice";
import { ProductParam } from "features/stock-ordering/core/stock-ordering.params";
import { OrderTableData } from "features/stock-ordering/core/domain/order-table-row.model";
import {
  FaAngleDown,
  FaAngleUp,
  FaBox,
  FaWarehouse,
  FaWeight,
} from "react-icons/fa";
import { selectGetAdminSession } from "features/admin/presentation/slices/get-admin-session.slice";

function not(a: readonly ProductArray[], b: readonly ProductArray[]) {
  return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a: readonly ProductArray[], b: readonly ProductArray[]) {
  return a.filter((value) => b.indexOf(value) !== -1);
}

function union(a: readonly ProductArray[], b: readonly ProductArray[]) {
  return [...a, ...not(b, a)];
}

interface StockOrderProductSelectorProps {
  setRows: ((rows: OrderTableData[]) => void) | undefined;
  company_id: number | undefined;
  supplier: number | null;
  category_id: string;
  selected_store: {
    store_id: string;
    name: string;
  };
}

interface ProductById {
  product_id: string;
  product_name: string;
  uom: string;
  cost: number;
  order_qty: number;
  uom_multiplier: number;
  currentStock: number;
  limit: number;
}

interface ProductArray extends Array<ProductById> {}

export function StockOrderProductSelector(
  props: StockOrderProductSelectorProps
) {
  const dispatch = useAppDispatch();
  const getProductInformation = useAppSelector(selectGetStockOrderProducts);
  const getAdminSessionState = useAppSelector(selectGetAdminSession);

  const { company } = getAdminSessionState.data?.admin.user_details ?? {};

  const [waitingForProducts, setWaitingForProducts] = useState(true);

  const [checked, setChecked] = useState<readonly ProductArray[]>([]);
  const [left, setLeft] = useState<readonly ProductArray[]>([]);
  const [right, setRight] = useState<readonly ProductArray[]>([]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const [openSearchType, setOpenSearchType] = useState(false);
  const [searchType, setSearchType] =
    useState<keyof ProductById>("product_name");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const productParams: ProductParam = {
      category: props.category_id,
      store_information: {
        store_id: props.selected_store.store_id,
        store_name: props.selected_store.name,
      },
      company_id: props.company_id,
      supplier: props.supplier,
    };

    dispatch(getStockOrderProducts(productParams));

    setLeft([]);
    setRight([]);
  }, [dispatch, props.category_id, props.selected_store]);

  useEffect(() => {
    if (getProductInformation.data?.products) {
      const leftData: ProductArray[] = getProductInformation.data.products
        .filter((product) => product.active === 1)
        .map((row) => [
          {
            product_id: row.product_id,
            product_name: row.product_name,
            uom: row.uom,
            cost: row.cost,
            order_qty: row.orderQty,
            uom_multiplier: row.uom_multiplier,
            currentStock: row.currentStock,
            limit: row.limit,
          },
        ]);

      setLeft(leftData);
      setWaitingForProducts(false);
    } else {
      setWaitingForProducts(true);
    }
  }, [getProductInformation.data?.products]);

  useEffect(() => {
    const rightProductData: OrderTableData[] = right.flatMap(
      (productArray: ProductArray) =>
        productArray.map((product: ProductById) => ({
          productId: product.product_id,
          productName: product.product_name,
          uom: product.uom,
          cost: product.cost,
          orderQty: product.order_qty,
          qty_per_order: 0,
          uom_multiplier: product.uom_multiplier,
          itemTotalCost: 0,
          limit: product.limit,
        }))
    );

    props.setRows?.(rightProductData);
  }, [right]);

  const handleToggle = (value: ProductArray) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = (items: readonly ProductArray[]) =>
    intersection(checked, items).length;

  const handleToggleAll = (items: readonly ProductArray[]) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const customList = (
    title: React.ReactNode,
    items: readonly ProductArray[]
  ) => (
    <Card>
      <CardHeader
        sx={{ px: 2, py: 1 }}
        avatar={
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={
              numberOfChecked(items) === items.length && items.length !== 0
            }
            indeterminate={
              numberOfChecked(items) !== items.length &&
              numberOfChecked(items) !== 0
            }
            disabled={items.length === 0}
            inputProps={{
              "aria-label": "all items selected",
            }}
          />
        }
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} selected`}
      />
      {title === "Products" && (
        <div className="px-2 py-1">
          <div className="flex space-x-2">
            <button
              onClick={() => setOpenSearchType(!openSearchType)}
              className="flex space-x-1"
            >
              {/* <span className="text-sm self-center">{searchType}</span> */}
              {openSearchType ? (
                <FaAngleUp className="self-center" size={20} />
              ) : (
                <FaAngleDown className="self-center" size={20} />
              )}
            </button>
            <input
              className="px-2 w-full rounded-sm border border-gray-400"
              placeholder={`Search by ${searchType}`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
            />
          </div>
          <div className={`px-1 ${!openSearchType && "hidden"}`}>
            <RadioGroup
              aria-labelledby="search-type"
              defaultValue="name"
              name="radio-buttons-group"
              row
              onChange={(event) =>
                setSearchType(
                  (event.target as HTMLInputElement).value as keyof ProductById
                )
              }
              value={searchType}
            >
              <FormControlLabel
                value="product_name"
                control={<Radio size="small" />}
                label="Name"
              />
              <FormControlLabel
                value="product_id"
                control={<Radio size="small" />}
                label="Product Id"
              />
              <FormControlLabel
                value="uom"
                control={<Radio size="small" />}
                label="Uom"
              />
            </RadioGroup>
          </div>
        </div>
      )}

      <Divider />

      {waitingForProducts ? (
        <div className="flex flex-col space-y-1 px-1">
          <Skeleton width={350} height={30} animation="wave" />
          <Skeleton width={350} height={30} animation="wave" />
          <Skeleton width={350} height={30} animation="wave" />
          <Skeleton width={350} height={30} animation="wave" />
        </div>
      ) : (
        <List
          sx={{
            width: 350,
            height: 230,
            bgcolor: "background.paper",
            overflow: "auto",
          }}
          dense
          component="div"
          role="list"
        >
          {items.map((value: ProductArray, index: number) => {
            const labelId = `transfer-list-all-item-${value}-label`;

            return (
              <React.Fragment key={index}>
                <ListItemButton role="listitem" onClick={handleToggle(value)}>
                  <ListItemIcon>
                    <Checkbox
                      checked={checked.indexOf(value) !== -1}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{
                        "aria-labelledby": labelId,
                      }}
                    />
                  </ListItemIcon>
                  <div className="flex flex-col text-xs font-[Roboto] py-0.5 space-y-0.5">
                    <p> {value.map((row) => row.product_id)}</p>

                    <p className="font-semibold">
                      {value.map((row) => row.product_name)}
                    </p>
                    <div className="flex space-x-3">
                      <div className="flex space-x-1">
                        <FaWeight className="self-center text-button text-[10px]" />
                        <span className="text-xs whitespace-nowrap">Uom:</span>

                        <p> {value.map((row) => row.uom)}</p>
                      </div>

                      {company?.company_id !== 1 && (
                        <div className="flex space-x-1">
                          <FaWarehouse className="self-center text-button text-[10px]" />
                          <span className="text-xs whitespace-nowrap">
                            Stock:
                          </span>
                          <p> {value.map((row) => row.currentStock)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </ListItemButton>
                <Divider variant="middle" />
              </React.Fragment>
            );
          })}
        </List>
      )}
    </Card>
  );

  return (
    <Grid container spacing={2} justifyContent="center" alignItems="center">
      <Grid item>
        {customList(
          "Products",
          left.filter((fltr) =>
            fltr[0][searchType]
              .toString()
              .toLowerCase()
              .includes(search.toLowerCase())
          )
        )}
      </Grid>
      <Grid item>
        <Grid container direction="column" alignItems="center">
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label="move selected right"
          >
            &gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label="move selected left"
          >
            &lt;
          </Button>
        </Grid>
      </Grid>
      <Grid item>{customList("Added Products", right)}</Grid>
    </Grid>
  );
}
